import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { ArrowLeft, Zap, Lightbulb, AlertTriangle, Users, TrendingUp, HelpCircle, ChevronDown, ChevronUp, Archive, CheckCircle2, Clock, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const CAT_ICONS = {
  idee: Lightbulb,
  decision: Zap,
  signal_rh: Users,
  opportunite: TrendingUp,
  inquietude: AlertTriangle,
  autre: HelpCircle,
};

const STATUS_COLORS = {
  en_attente: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  traitee: 'bg-green-500/10 text-green-400 border-green-500/30',
  archivee: 'bg-atlasTextSecondary/10 text-atlasTextSecondary border-atlasBorder',
};

const CaptureJournal = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useTranslation();
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filterCat, setFilterCat] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetchCaptures(); }, [filterCat, filterStatus]);

  const fetchCaptures = async () => {
    try {
      let url = '/api/capture/list?limit=100';
      if (filterCat) url += `&categorie=${filterCat}`;
      if (filterStatus) url += `&statut=${filterStatus}`;
      const res = await api.get(url);
      setCaptures(res.data.captures || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, statut) => {
    try {
      await api.put(`/api/capture/${id}/status`, { statut });
      setCaptures(prev => prev.map(c => c.id === id ? { ...c, statut } : c));
      toast.success(t('common.success'));
    } catch { toast.error(t('common.error')); }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-atlasCyan animate-pulse">{t('common.loading')}</div></div>;
  }

  return (
    <div data-testid="capture-journal-page" className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/profile')} data-testid="captures-back-btn" className="p-2 rounded-lg text-atlasTextSecondary hover:text-white hover:bg-white/5 transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{t('capture.journalTitle')}</h1>
          <p className="text-sm text-atlasTextSecondary">{t('capture.journalSubtitle')}</p>
        </div>
        <button data-testid="captures-filter-btn" onClick={() => setShowFilters(!showFilters)}
          className={`p-2 rounded-lg transition-all ${showFilters ? 'bg-yellow-500/10 text-yellow-400' : 'text-atlasTextSecondary hover:text-white hover:bg-white/5'}`}>
          <Filter className="w-5 h-5" />
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div data-testid="captures-filters" className="glass-card p-4 mb-4 space-y-3">
          <div>
            <p className="text-xs text-atlasTextSecondary mb-2">{t('capture.filterCategory')}</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilterCat(null)} className={`px-3 py-1 rounded-full text-xs transition-all ${!filterCat ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-atlasTextSecondary hover:text-white'}`}>{t('capture.all')}</button>
              {Object.keys(CAT_ICONS).map(cat => (
                <button key={cat} onClick={() => setFilterCat(cat === filterCat ? null : cat)}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${filterCat === cat ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-atlasTextSecondary hover:text-white'}`}>
                  {t(`capture.cat.${cat}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-atlasTextSecondary mb-2">{t('capture.filterStatus')}</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilterStatus(null)} className={`px-3 py-1 rounded-full text-xs transition-all ${!filterStatus ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-atlasTextSecondary hover:text-white'}`}>{t('capture.all')}</button>
              {['en_attente', 'traitee', 'archivee'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s === filterStatus ? null : s)}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${filterStatus === s ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-atlasTextSecondary hover:text-white'}`}>
                  {t(`capture.status.${s}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {captures.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <Zap className="w-10 h-10 mx-auto mb-4" style={{ color: '#FFD60A' }} />
          <p className="text-white font-medium mb-1">{t('capture.emptyTitle')}</p>
          <p className="text-sm text-atlasTextSecondary">{t('capture.emptyDesc')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {captures.map(cap => {
            const isExp = expanded === cap.id;
            const CatIcon = CAT_ICONS[cap.categorie] || HelpCircle;
            return (
              <div key={cap.id} data-testid={`capture-card-${cap.id}`} className="glass-card overflow-hidden">
                <button onClick={() => setExpanded(isExp ? null : cap.id)} className="w-full flex items-start gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors">
                  <div className="p-2 rounded-lg bg-yellow-500/10 flex-shrink-0 mt-0.5">
                    <CatIcon className="w-4 h-4" style={{ color: '#FFD60A' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-xs text-atlasTextSecondary">{formatDate(cap.created_at)} · {cap.jour_semaine}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[cap.statut] || ''}`}>
                        {t(`capture.status.${cap.statut}`)}
                      </span>
                    </div>
                    <p className="text-sm text-white line-clamp-2">{cap.contenu_texte}</p>
                    <p className="text-xs mt-1" style={{ color: '#FFD60A' }}>{t(`capture.cat.${cap.categorie}`)}</p>
                  </div>
                  <div className="flex-shrink-0 mt-1">
                    {isExp ? <ChevronUp className="w-4 h-4 text-atlasTextSecondary" /> : <ChevronDown className="w-4 h-4 text-atlasTextSecondary" />}
                  </div>
                </button>
                {isExp && (
                  <div className="px-4 pb-4 space-y-3 border-t border-atlasBorder pt-3">
                    <p className="text-sm text-white whitespace-pre-wrap">{cap.contenu_texte}</p>
                    <div className="flex flex-wrap gap-2">
                      {cap.statut !== 'traitee' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(cap.id, 'traitee')}
                          data-testid={`capture-mark-treated-${cap.id}`}
                          className="text-green-400 border-green-500/30 hover:bg-green-500/10 text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" />{t('capture.markTreated')}
                        </Button>
                      )}
                      {cap.statut !== 'archivee' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(cap.id, 'archivee')}
                          data-testid={`capture-archive-${cap.id}`}
                          className="text-atlasTextSecondary border-atlasBorder hover:bg-white/5 text-xs">
                          <Archive className="w-3 h-3 mr-1" />{t('capture.archive')}
                        </Button>
                      )}
                      {cap.statut === 'archivee' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(cap.id, 'en_attente')}
                          className="text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10 text-xs">
                          <Clock className="w-3 h-3 mr-1" />{t('capture.reopen')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CaptureJournal;
